import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  Typography,
  Box,
  Popover,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Phone as PhoneIcon,
  AddBox as AddBoxIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PropertyListButton, ResponsiveLogo, NavLink } from "./style";
import { ROUTES } from "@constants/routes"; // Import centralized routes
import logo from "@assets/images/logo.webp";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [phoneAnchorEl, setPhoneAnchorEl] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  
  // Updated to use centralized routes
  const whiteBackgroundRoutes = [
    ROUTES.BLOG_LIST,
    `${ROUTES.BLOG_LIST}/`,
    // Dynamic routes - we'll check these with regex patterns
  ];

  // Check for white background pages using centralized routes
  const isWhiteBackgroundPage =
    whiteBackgroundRoutes.includes(location.pathname) ||
    // Blog detail pages
    new RegExp(`^${ROUTES.BLOG_DETAIL().replace(':slug', '[^/]+').replace('\\', '\\\\')}$`).test(location.pathname) ||
    // Area detail pages  
    new RegExp(`^${ROUTES.AREA_DETAIL().replace(':title', '[^/]+').replace('\\', '\\\\')}$`).test(location.pathname) ||
    // Property detail pages
    new RegExp(`^${ROUTES.PROPERTIES().replace(':title', '[^/]+').replace('\\', '\\\\')}$`).test(location.pathname) ||
    // Developer detail pages
    new RegExp(`^${ROUTES.DEVELOPER_DETAIL().replace(':title', '[^/]+').replace('\\', '\\\\')}$`).test(location.pathname) ||
    // Featured property detail pages
    new RegExp(`^${ROUTES.FEATURED_PROPERTY_DETAIL().replace(':slug', '[^/]+').replace('\\', '\\\\')}$`).test(location.pathname) ||
    // Project detail pages
    new RegExp(`^${ROUTES.PROJECT_DETAIL().replace(':id', '[^/]+').replace('\\', '\\\\')}$`).test(location.pathname);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    // Initial call to set scroll state
    handleScroll();
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {}, [scrolled]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Updated to use centralized route
  const handlePropertyListClick = () => {
    navigate(ROUTES.LIST_PROPERTY);
  };

  const handlePhoneIconClick = (event) => {
    setPhoneAnchorEl(event.currentTarget);
  };

  const handlePhonePopoverClose = () => {
    setPhoneAnchorEl(null);
  };

  const handleSubmenuClick = (index, item) => {
    if (isMobile) {
      // If submenu is already open, close it
      if (openSubmenu === index) {
        setOpenSubmenu(null);
      } else {
        // If submenu is closed, navigate to the main item and open submenu
        setOpenSubmenu(index);
        if (item.path) {
          navigate(item.path);
        }
      }
    }
  };

  const handleSubmenuMouseEnter = (index) => {
    if (!isMobile) {
      setOpenSubmenu(index);
    }
  };

  const handleSubmenuMouseLeave = () => {
    if (!isMobile) {
      setOpenSubmenu(null);
    }
  };

  // Updated navigation items to use centralized routes
  const navItems = [
    { label: "Buy", path: ROUTES.BUY },
    { label: "Rent", path: ROUTES.RENT },
    {
      label: "Off Plan",
      path: ROUTES.OFF_PLAN,
      subItems: [
        { label: "Projects", path: ROUTES.PROJECTS },
        { label: "Developers", path: ROUTES.DEVELOPERS },
      ],
    },
    { label: "Areas", path: ROUTES.AREAS },
    { label: "Services", path: ROUTES.SERVICES },
    { label: "About Us", path: ROUTES.ABOUT_US },
    { label: "Blogs", path: ROUTES.BLOG_LIST },
    { label: "Contact Us", path: ROUTES.CONTACT_US },
  ];

  const renderNavItems = (items, mobile = false, scrolled = false) => {
    return (
      <List
        sx={{
          p: mobile ? 2 : 0,
          display: "flex",
          flexDirection: mobile ? "column" : "row",
          alignItems: mobile ? "flex-start" : "center",
          width: mobile ? "100%" : "auto",
          textTransform: "lowercase",
        }}
      >
        {items.map((item, index) => (
          <ListItem
            key={index}
            disablePadding
            sx={{
              width: "auto",
              ...(mobile ? { mb: 1 } : { display: "inline-block", mx: 1 }),
              color:
                isMobile || scrolled || isWhiteBackgroundPage
                  ? "black"
                  : "white",
              filter:
                isMobile || scrolled || isWhiteBackgroundPage
                  ? "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.1))" // softer shadow on light background
                  : "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))", // stronger shadow on dark background
            }}
          >
            {item.subItems ? (
              <Box
                sx={{
                  position: "relative",
                  // Hover behavior only for desktop
                  ...(!isMobile && {
                    "&:hover > div": { display: "block" },
                  }),
                  zIndex: 1, // parent stays below dropdown
                  color:
                    isMobile || scrolled || isWhiteBackgroundPage
                      ? "black"
                      : "white",
                  textShadow: 0,
                }}
                onMouseEnter={() => handleSubmenuMouseEnter(index)}
                onMouseLeave={handleSubmenuMouseLeave}
              >
                <NavLink
                  component={Link}
                  to={item.path}
                  onClick={(e) => {
                    if (isMobile) {
                      e.preventDefault();
                      handleSubmenuClick(index, item);
                    }
                  }}
                  isactive={
                    !isMobile
                      ? (location.pathname === item.path).toString()
                      : undefined
                  }
                  sx={{
                    color:
                      isMobile || scrolled || isWhiteBackgroundPage
                        ? "black"
                        : "white",
                    transition: "color 0.3s ease",
                    textTransform: "lowercase",
                    cursor: "pointer",

                    // Add arrow indicator for mobile
                    ...(isMobile && {
                      "&::after": {
                        content: openSubmenu === index ? '"▼"' : '"▶"',
                        marginLeft: "0.5rem",
                        fontSize: "0.8rem",
                      },
                    }),
                  }}
                >
                  {item.label}
                </NavLink>
                <Box
                  sx={{
                    // Show/hide logic based on screen size
                    display: !isMobile
                      ? "none" // Desktop: controlled by hover
                      : openSubmenu === index
                      ? "block"
                      : "none", // Mobile: controlled by click
                    position: !isMobile ? "absolute" : "static",
                    backgroundColor: "white",
                    boxShadow: !isMobile ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
                    zIndex: 999, // increased to make sure it's on top
                    borderRadius: !isMobile ? 2 : 0,
                    color: "black",
                    // Mobile specific styles
                    ...(isMobile && {
                      marginTop: "0.5rem",
                      paddingLeft: "1rem",
                      borderLeft: "2px solid rgba(0,0,0,0.1)",
                      borderRadius: "10px",
                    }),
                  }}
                >
                  {item.subItems.map((subItem, subIndex) => (
                    <NavLink
                      key={subIndex}
                      component={Link}
                      to={subItem.path}
                      onClick={() => {
                        if (isMobile) {
                          setOpenSubmenu(null); // Close submenu on mobile after click
                          setMobileOpen(false); // Also close the mobile drawer
                        }
                      }}
                      sx={{
                        display: "block",
                        px: !isMobile ? 2 : 0,
                        py: 1,
                        "&:hover": {
                          backgroundColor: !isMobile
                            ? "rgba(0,0,0,0.05)"
                            : "transparent",
                        },
                        textTransform: "lowercase",
                        color: "black",
                      }}
                    >
                      {subItem.label}
                    </NavLink>
                  ))}
                </Box>
              </Box>
            ) : (
              <NavLink
                component={Link}
                to={item.path}
                onClick={() => {
                  if (mobile) {
                    setMobileOpen(false); // Close mobile drawer on navigation
                  }
                }}
                isactive={(location.pathname === item.path).toString()}
                sx={{
                  textTransform: "lowercase",
                  color:
                    isMobile || scrolled || isWhiteBackgroundPage
                      ? "black"
                      : "white",
                }}
              >
                {item.label}
              </NavLink>
            )}
          </ListItem>
        ))}
      </List>
    );
  };

  const phonePopoverOpen = Boolean(phoneAnchorEl);

  return (
    <Box
      sx={{
        position: "fixed",
        // position: "relative",
        width: "100%",
        zIndex: 999,
        top: 0,
        backgroundColor: "transparent",
      }}
    >
      <AppBar
        position="sticky"
        elevation={scrolled ? 4 : 0}
        sx={{
          boxShadow: scrolled ? "0px 2px 10px rgba(0, 0, 0, 0.1)" : "none",
          backgroundColor: scrolled ? "white" : "transparent",
          transition: "background-color 0.3s ease, box-shadow 0.3s ease",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 1, md: 4 },
            height: "80px",
          }}
        >
          {/* Left Side Logo - Updated to use centralized route */}
          <Link
            to={ROUTES.HOME}
            style={{
              display: "flex",
              alignItems: isMobile ? "center" : "flex-end", // Align to bottom vertically
              height: "100%",
            }}
          >
            <ResponsiveLogo src={logo} alt="Header Logo" />
          </Link>

          {/* Right Side Content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 2 },
            }}
          >
            {isMobile ? (
              <>
                <IconButton size="small" onClick={handlePhoneIconClick}>
                  <PhoneIcon fontSize="small" />
                </IconButton>
                <Popover
                  open={phonePopoverOpen}
                  anchorEl={phoneAnchorEl}
                  onClose={handlePhonePopoverClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <Typography
                    sx={{
                      p: 2,
                    }}
                  >
                    +971 4 576 0110
                  </Typography>
                </Popover>

                <IconButton size="small" onClick={handlePropertyListClick}>
                  <AddBoxIcon fontSize="small" />
                </IconButton>

                <IconButton size="small" onClick={handleDrawerToggle}>
                  <MenuIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color:
                      scrolled || isWhiteBackgroundPage ? "black" : "white",
                    filter:
                      isMobile || scrolled || isWhiteBackgroundPage
                        ? "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.1))" // softer shadow on light background
                        : "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.8))", // stronger shadow on dark background
                  }}
                >
                  <PhoneIcon
                    fontSize="small"
                    sx={{
                      filter: "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3))",
                    }}
                  />{" "}
                  +971 4 576 0110
                </Typography>

                <PropertyListButton
                  variant="outlined"
                  onClick={handlePropertyListClick}
                >
                  List With Us Now!
                </PropertyListButton>
              </>
            )}
          </Box>
        </Toolbar>

        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1,
            }}
          >
            {renderNavItems(navItems, false, scrolled)}
          </Box>
        )}
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: "70%",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
          }}
        >
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        {renderNavItems(navItems, true)}
      </Drawer>
    </Box>
  );
};

export default Header;